import { commerceConfig, newsletterConfig } from './site-config.js';

const isWebUrl = (value) => /^https:\/\//i.test(value || '');

// mailto: URIs need RFC 6068 percent-encoding; URLSearchParams would encode
// spaces as "+", which some mail clients display literally.
function mailtoUrl(subject, body) {
    return `mailto:${commerceConfig.inquiryEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Older links used per-tier service slugs; map them onto the current offers.
const legacyServiceMap = {
    readiness: 'session',
    guided: 'session',
    submission: 'launch',
    screenshots: 'launch',
    aso: 'launch',
    launch: 'launch'
};

function selectRequestedService(form) {
    const requested = new URLSearchParams(window.location.search).get('service');
    if (!requested) return;

    const serviceField = form.elements.namedItem('service');
    if (!serviceField) return;

    const value = legacyServiceMap[requested] || requested;
    const matchingOption = Array.from(serviceField.options).find((option) => option.value === value);
    if (matchingOption) serviceField.value = value;
}

function configureServiceLinks(form) {
    document.querySelectorAll('[data-service-link]').forEach((link) => {
        link.addEventListener('click', () => {
            const serviceField = form.elements.namedItem('service');
            if (serviceField) serviceField.value = link.dataset.serviceLink;
        });
    });
}

function configureInquiryForm() {
    const form = document.querySelector('[data-inquiry-form]');
    if (!form) return;

    selectRequestedService(form);
    configureServiceLinks(form);

    const status = form.querySelector('[data-form-status]');
    const submitButton = form.querySelector('button[type="submit"]');
    const tokenField = form.querySelector('[data-turnstile-token]');
    const turnstileContainer = form.querySelector('[data-turnstile-container]');
    let turnstileWidgetId;

    const setStatus = (message, state = '') => {
        if (!status) return;
        status.classList.toggle('is-success', state === 'success');
        status.classList.toggle('is-error', state === 'error');
        status.textContent = message;
        status.hidden = !message;
    };

    const showSendError = (message) => {
        if (!status) return;
        setStatus('', 'error');
        const emailLink = document.createElement('a');
        emailLink.href = `mailto:${commerceConfig.inquiryEmail}`;
        emailLink.textContent = commerceConfig.inquiryEmail;
        status.append(
            document.createTextNode(`${message} You can also email `),
            emailLink,
            document.createTextNode('.')
        );
        status.hidden = false;
    };

    const waitForTurnstile = () => new Promise((resolve, reject) => {
        const deadline = Date.now() + 8000;
        const check = () => {
            if (window.turnstile) {
                resolve(window.turnstile);
            } else if (Date.now() >= deadline) {
                reject(new Error('Turnstile did not load'));
            } else {
                setTimeout(check, 100);
            }
        };
        check();
    });

    const getTurnstileSiteKey = async () => {
        try {
            const response = await fetch('/api/inquiry/config', {
                headers: { Accept: 'application/json' }
            });
            const result = await response.json();
            if (response.ok && result.turnstileSiteKey) return result.turnstileSiteKey;
        } catch {
            // Vite's static dev server has no Worker API. Its localhost-only
            // fallback still renders Turnstile for layout and interaction QA.
        }

        if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
            return '1x00000000000000000000AA';
        }
        throw new Error('Inquiry form is not configured');
    };

    const configureTurnstile = async () => {
        if (!turnstileContainer || !tokenField) return;
        try {
            const [turnstile, sitekey] = await Promise.all([
                waitForTurnstile(),
                getTurnstileSiteKey()
            ]);
            turnstileWidgetId = turnstile.render(turnstileContainer, {
                sitekey,
                theme: 'dark',
                callback: (token) => {
                    tokenField.value = token;
                    if (status?.textContent.startsWith('Complete the security check')) setStatus('');
                },
                'expired-callback': () => {
                    tokenField.value = '';
                    setStatus('The security check expired. Complete it again before sending.', 'error');
                },
                'error-callback': () => {
                    tokenField.value = '';
                    showSendError('The security check could not load.');
                }
            });
        } catch {
            showSendError('The inquiry form is temporarily unavailable.');
        }
    };

    configureTurnstile();

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const data = new FormData(form);
        if (!data.get('turnstileToken')) {
            setStatus('Complete the security check before sending.', 'error');
            return;
        }

        const payload = Object.fromEntries(data.entries());
        const replyEmail = data.get('email');
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = 'Sending…';
        }
        form.setAttribute('aria-busy', 'true');
        setStatus('Sending your project…');

        try {
            const response = await fetch('/api/inquiry', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.ok) {
                throw new Error(result.error || 'The inquiry could not be sent.');
            }

            form.reset();
            if (window.turnstile && turnstileWidgetId !== undefined) {
                window.turnstile.reset(turnstileWidgetId);
            }
            setStatus(`Project sent. I'll reply to ${replyEmail}.`, 'success');
        } catch (error) {
            showSendError(error.message || 'The inquiry could not be sent.');
            if (window.turnstile && turnstileWidgetId !== undefined) {
                window.turnstile.reset(turnstileWidgetId);
            }
            if (tokenField) tokenField.value = '';
        } finally {
            form.removeAttribute('aria-busy');
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = 'Send the project';
            }
        }
    });
}

// Scripts added via innerHTML never execute, so rebuild each one as a live
// element before inserting the snippet.
function injectEmbedCode(container, code) {
    const template = document.createElement('template');
    template.innerHTML = code;
    template.content.querySelectorAll('script').forEach((inert) => {
        const script = document.createElement('script');
        Array.from(inert.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
        script.textContent = inert.textContent;
        inert.replaceWith(script);
    });
    container.append(...template.content.childNodes);
}

// Each .newsletter-slot names its form via data-newsletter-slot ("lab" by
// default, "guide" on the guide page). A slot holds a fixed height through
// three states: a quiet loading strip while the third-party embed
// initializes invisibly behind it, then a cross-fade to the embed at
// identical dimensions – or, if the embed never sizes itself, the working
// email fallback form.
const fallbackMail = {
    lab: {
        subject: 'Subscribe me to This Week in the Lab',
        body: (email) => `Hi Jeremy,\n\nPlease add me to the This Week in the Lab newsletter.\n\nEmail: ${email}\n`,
        status: 'Opening your email app to confirm your subscription.'
    },
    guide: {
        subject: 'Send me the Ship Your iPhone App guide',
        body: (email) => `Hi Jeremy,\n\nPlease send me the Ship Your iPhone App guide and add me to This Week in the Lab.\n\nEmail: ${email}\n`,
        status: 'Opening your email app to request the guide.'
    }
};

function configureNewsletterSlot(slot) {
    const key = slot.dataset.newsletterSlot || 'lab';
    const config = (newsletterConfig.forms && newsletterConfig.forms[key]) || {};
    const form = slot.querySelector('[data-newsletter-form]');
    const embed = slot.querySelector('[data-newsletter-embed]');
    const loading = slot.querySelector('[data-newsletter-loading]');
    const status = slot.parentElement.querySelector('[data-newsletter-status]');
    if (!form && !embed) return;

    // Keep the email fallback functional even when an embed is configured but
    // fails to load. Never submit the email as a page query string.
    if (form) {
        const mail = fallbackMail[key] || fallbackMail.lab;
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            const email = new FormData(form).get('email') || '';
            if (status) {
                status.textContent = mail.status;
                status.hidden = false;
            }
            window.location.href = mailtoUrl(mail.subject, mail.body(email));
        });
    }

    let embedObserver;
    const revealEmbed = () => {
        embedObserver?.disconnect();
        embed.classList.add('is-live');
        if (loading) loading.hidden = true;
        if (form) form.hidden = true;
    };

    const revealFallback = () => {
        if (loading) loading.hidden = true;
        if (form) form.hidden = false;
    };

    const embedCode = (config.embedCode || '').trim();
    if (embed && embedCode) {
        if (loading) loading.hidden = false;
        if (form && loading) form.hidden = true;
        const isSized = () => {
            const wrapper = embed.querySelector(':scope > div');
            const height = wrapper ? wrapper.getBoundingClientRect().height : 0;
            return height >= 40 && height <= 400;
        };
        // A lower-page embed can initialize after the fallback timeout. Watch
        // its own wrapper so each placement can recover independently.
        embedObserver = new MutationObserver(() => {
            if (isSized()) revealEmbed();
        });
        embedObserver.observe(embed, { childList: true, subtree: true, attributes: true });
        injectEmbedCode(embed, embedCode);
        const deadline = Date.now() + 5000;
        const waitForSize = () => {
            // The loader's outer wrapper stays at height 0 until the form
            // inside it has actually initialized.
            if (isSized()) {
                revealEmbed();
            } else if (Date.now() < deadline) {
                setTimeout(waitForSize, 150);
            } else {
                revealFallback();
            }
        };
        waitForSize();
    } else if (embed && isWebUrl(config.embedUrl)) {
        if (loading) loading.hidden = false;
        if (form && loading) form.hidden = true;
        const iframe = document.createElement('iframe');
        iframe.src = config.embedUrl;
        iframe.title = key === 'guide' ? 'Get the Ship Your iPhone App guide' : 'Subscribe to This Week in the Lab';
        iframe.style.height = '3.5rem';
        iframe.setAttribute('data-test-id', 'beehiiv-embed');
        iframe.addEventListener('load', revealEmbed);
        embed.appendChild(iframe);
    }
}

function configureNewsletter() {
    document.querySelectorAll('.newsletter-slot').forEach(configureNewsletterSlot);
}

document.addEventListener('DOMContentLoaded', () => {
    configureInquiryForm();
    configureNewsletter();
});
